import { 
    Flex, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, 
    Box, Text, Input, Center, useColorModeValue,
    Icon, Alert, AlertIcon, Badge, Tooltip, Stack, HStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Divider,
    Grid,
    GridItem,
} from '@chakra-ui/react';
import { 
    FaSearch, FaSort, FaSortUp, FaSortDown, FaFileInvoice,
    FaGift, FaUser, FaPhone, FaCoins, FaCalendarAlt, FaUserTie, FaTicketAlt,
    FaChevronRight, FaChevronLeft,
    FaPhoneAlt,
    FaClock,
    FaHashtag,
} from 'react-icons/fa';
import React, { useEffect, useState, useCallback } from "react";
import { useGetUsersRewardsDataMutation } from "../../slices/adminApiSlice";
import Loader from "../../components/Loader";
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/react';
import AdminAddReward from '../../components/AdminComponents/AdminAddReward';

const RewardRedemption = () => {
    const navigate = useNavigate();
    const [rewardsData, setRewardsData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;
    const { isOpen, onClose } = useDisclosure();
    // Redeem Details Modal
    const {
        isOpen: isRedeemOpen,
        onOpen: onRedeemOpen,
        onClose: onRedeemClose,
    } = useDisclosure();
    const [getRewardsData, { isLoading, error: fetchError }] = useGetUsersRewardsDataMutation();
    // const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Color scheme
    const colorScheme = 'teal';
    const headerBgColor = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.700`);
    const tblThBgColor = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.800`);
    const hoverColor = useColorModeValue(`${colorScheme}.50`, 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const inputBg = useColorModeValue('white', 'gray.700');
    const paginationBg = useColorModeValue('gray.50', 'gray.800');
    //const headerColor = useColorModeValue('gray.700', 'white');

    // Fetch rewards data
    const fetchRewards = useCallback(async () => {
        try {
            const response = await getRewardsData().unwrap();
            setRewardsData(response.data);
        } catch (error) {
            console.error('Error fetching user rewards:', error);
        }
    }, [getRewardsData]);

    useEffect(() => {
        fetchRewards();
    }, [fetchRewards]);

    const handleSort = (column) => {
        const columnMap = {
            invoice: "invoice_id",
            title: "reward.title",
            username: "user.name",
            phone: "user.phone",
            amount: "amount",
            type: "type",
            redeemedpts: "reward.points_required",
            redeemeddate: "createdAt",
            redeemedby: "added_by_name",
        };
    
        const mappedColumn = columnMap[column] || column;
        setSortOrder(sortColumn === mappedColumn && sortOrder === "asc" ? "desc" : "asc");
        setSortColumn(mappedColumn);
    };

    const sortedRewards = [...rewardsData].sort((a, b) => {
        let aValue, bValue;
    
        // Handle nested fields
        switch (sortColumn) {
            case 'invoice_id':
                aValue = a.invoice_id;
                bValue = b.invoice_id;
                break;
            case 'reward.title':
                aValue = a.reward?.title;
                bValue = b.reward?.title;
                break;
            case 'user.name':
                aValue = a.user?.name;
                bValue = b.user?.name;
                break;
            case 'user.phone':
                aValue = a.user?.phone;
                bValue = b.user?.phone;
                break;
            case 'amount':
                aValue = a.amount;
                bValue = b.amount;
                break;
            case 'type':
                aValue = a.type;
                bValue = b.type;
                break;
            case 'reward.points_required':
                aValue = a.reward?.points_required;
                bValue = b.reward?.points_required;
                break;
            case 'createdAt':
                aValue = new Date(a.createdAt).getTime();
                bValue = new Date(b.createdAt).getTime();
                break;
            case 'added_by_name':
                aValue = a.added_by_name;
                bValue = b.added_by_name;
                break;
            default:
                aValue = a[sortColumn];
                bValue = b[sortColumn];
        }
    
        // Handle undefined or null values
        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';
    
        // Convert values to appropriate types before sorting
        if (!isNaN(aValue) && !isNaN(bValue)) {
            aValue = Number(aValue);
            bValue = Number(bValue);
        } else if (sortColumn === 'createdAt') {
            // Already handled
        } else {
            aValue = aValue.toString().toLowerCase();
            bValue = bValue.toString().toLowerCase();
        }
    
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    // Filtering function
    const filteredRewards = sortedRewards.filter(transaction => {
        const query = searchQuery.toLowerCase();
        return (
            transaction.invoice_id?.toLowerCase().includes(query) ||
            transaction.user?.name?.toLowerCase().includes(query) ||
            transaction.user?.phone?.includes(query) ||
            transaction.reward?.title?.toLowerCase().includes(query) ||
            new Date(transaction.createdAt).toISOString().toLowerCase().includes(query)
        );
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredRewards.length / usersPerPage);
    const paginatedRewards = filteredRewards.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage);

    // Column configuration with icons
    const columns = [
        { 
          key: 'invoice', 
          label: 'Redeem ID', 
          icon: FaFileInvoice,
          colorScheme: 'blue',
          tooltip: 'Sort by Redeem ID'
        },
        { 
          key: 'title', 
          label: 'Title', 
          icon: FaGift,
          colorScheme: 'purple',
          tooltip: 'Sort by Title'
        },
        { 
          key: 'username', 
          label: 'User Name', 
          icon: FaUser,
          colorScheme: 'teal',
          tooltip: 'Sort by User Name'
        },
        { 
          key: 'phone', 
          label: 'Phone', 
          icon: FaPhone,
          colorScheme: 'green',
          tooltip: 'Sort by Phone'
        },
        { 
          key: 'amount', 
          label: 'Amount', 
          icon: FaCoins,
          colorScheme: 'orange',
          tooltip: 'Sort by Amount'
        },
        { 
            key: 'type', 
            label: 'Type', 
            icon: FaTicketAlt,
            colorScheme: 'gray',
            tooltip: 'Sort by Reward Type'
          },
        { 
          key: 'redeemedpts', 
          label: 'Redeemed Pts', 
          icon: FaCoins,
          colorScheme: 'yellow',
          tooltip: 'Sort by Redeemed Pts'
        },
        { 
          key: 'redeemeddate', 
          label: 'Redeemed Date', 
          icon: FaCalendarAlt,
          colorScheme: 'red',
          tooltip: 'Sort by Redemption Date'
        },
        { 
          key: 'redeemedby', 
          label: 'Redeemed By', 
          icon: FaUserTie,
          colorScheme: 'cyan',
          tooltip: 'Sort by admin who processed'
        }
      ];

    return (
        <Box p={4} maxW="100vw" overflowX="auto">
            {/* <Heading size="lg" mb={6} color={headerColor}>
                Reward Redemption Management
            </Heading> */}

            {/* Action Buttons */}
            <Box mb={6}>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                    <Button 
                        colorScheme={colorScheme} 
                        variant="outline" 
                        leftIcon={<Icon as={FaGift} />}
                        onClick={() => navigate('/admin/points-redeemption')}
                        width={{ base: 'full', md: 'auto' }}
                    >
                        Redeem Points
                    </Button>
                    <Button 
                        colorScheme={colorScheme} 
                        variant="outline" 
                        leftIcon={<Icon as={FaGift} />}
                        onClick={() => navigate('/admin/stamps-redeemption')}
                        width={{ base: 'full', md: 'auto' }}
                    >
                        Redeem Stamps
                    </Button>
                    <AdminAddReward isOpen={isOpen} onClose={onClose} />
                </Stack>
            </Box>

            {/* Search Input */}
            <Box mb={6} position="relative">
                <Input 
                    placeholder="Search rewards..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    size="lg" 
                    borderRadius="lg" 
                    bg={inputBg}
                    pl={10}
                    _focus={{ 
                        borderColor: `${colorScheme}.500`,
                        boxShadow: `0 0 0 1px ${useColorModeValue(`${colorScheme}.300`, `${colorScheme}.600`)}`
                    }}
                />
                <Icon 
                    as={FaSearch} 
                    position="absolute" 
                    left={3} 
                    top="50%" 
                    transform="translateY(-50%)" 
                    color="gray.400"
                />
            </Box>

            {/* Error Handling */}
            {fetchError && (
                <Alert status="error" borderRadius="lg" mb={6}>
                    <AlertIcon />
                    Error fetching rewards data: {fetchError.message}
                </Alert>
            )}

            {/* Table */}
            <TableContainer 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor={borderColor}
                bg={inputBg}
                boxShadow="sm"
            >
                {isLoading ? (
                    <Center><Loader color={inputBg} minH='50vh' /></Center>
                ) : paginatedRewards.length > 0 ? (
                    <Table variant="striped" size={'sm'} colorScheme={`${colorScheme}Alpha`}>
                        <Thead bg={headerBgColor}>
                        <Tr>
                            {columns.map((column) => (
                            <Th
                                key={column.key}
                                color="white"
                                onClick={() => handleSort(column.key)}
                                cursor="pointer"
                                _hover={{
                                bg: tblThBgColor,
                                transform: 'translateY(-2px)'
                                }}
                                transition="all 0.2s"
                                py={4}
                            >
                                <Tooltip 
                                label={column.tooltip} 
                                hasArrow 
                                placement="top"
                                bg={`${column.colorScheme}.500`}
                                >
                                <Flex align="center" gap={2}>
                                    <Center
                                    w={8}
                                    h={8}
                                    borderRadius="md"
                                    bg={`${column.colorScheme}.100`}
                                    color={`${column.colorScheme}.600`}
                                    >
                                    <Icon as={column.icon} boxSize={4} />
                                    </Center>
                                    <Text fontWeight="semibold">{column.label}</Text>
                                    <Icon
                                    as={
                                        sortColumn === column.key
                                        ? sortOrder === 'asc'
                                            ? FaSortUp
                                            : FaSortDown
                                        : FaSort
                                    }
                                    opacity={sortColumn === column.key ? 1 : 0.3}
                                    ml={1}
                                    />
                                </Flex>
                                </Tooltip>
                            </Th>
                            ))}
                        </Tr>
                        </Thead>
                        
                        <Tbody>
                            {paginatedRewards.map((history, index) => (
                                <Tr 
                                    key={`${history?.reward?._id || 'reward'}-${index}`} 
                                    _hover={{ bg: hoverColor }}
                                    transition="background-color 0.2s"
                                >
                                    {/* <Td>
                                        <Tooltip label={history.invoice_id}>
                                            <Text isTruncated maxW="150px">
                                                {history.invoice_id || 'N/A'}
                                            </Text>
                                        </Tooltip>
                                    </Td> */}
                                    <Td>
                                    <Tooltip label="Click to view redeem details">
                                        <Text
                                        isTruncated
                                        maxW="150px"
                                        cursor="pointer"
                                        color="blue.500"
                                        fontWeight="medium"
                                        _hover={{ textDecoration: 'underline' }}
                                        onClick={() => {
                                            setSelectedInvoice(history);
                                            onRedeemOpen();
                                        }}
                                        >
                                        {history.invoice_id || 'N/A'}
                                        </Text>
                                    </Tooltip>
                                    </Td>

                                    <Td>
                                        <Badge 
                                            colorScheme={history.reward?.title ? colorScheme : 'gray'} 
                                            variant="subtle"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                        >
                                            {history.reward?.title || 'N/A'}
                                        </Badge>
                                    </Td>
                                    <Td>{history.user?.name || 'N/A'}</Td>
                                    <Td>{history.user?.phone || 'N/A'}</Td>
                                    <Td fontWeight="semibold">
                                        {history.amount ? `${history.amount}` : 'N/A'}
                                    </Td>
                                    <Td>
                                        <Badge 
                                            colorScheme={'gray'}
                                            variant="subtle"
                                            px={2}
                                            py={1}
                                            borderRadius="full"
                                            >
                                            {
                                                history.type === 'redeem' ? 'Point' 
                                                    : history.type === 'redeem_stamp' ? 'Stamp' 
                                                    : null
                                            }
                                        </Badge>
                                    </Td>
                                    <Td textAlign="center">
                                        <Badge 
                                            colorScheme="orange" 
                                            variant="solid"
                                            borderRadius="full"
                                            px={3}
                                            py={1}
                                        >
                                            {history.reward?.points_required || 0}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        {history.createdAt ? 
                                            new Date(history.createdAt).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            }) : 
                                            'N/A'}
                                    </Td>
                                    <Td>
                                        <Text fontWeight="medium">
                                            {history.added_by_name || 'N/A'}
                                        </Text>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                ) : (
                    <Center minH="200px">
                        <Alert status="info" borderRadius="md" maxW="500px">
                            <AlertIcon />
                            {searchQuery ? 'No rewards match your search criteria' : 'No rewards found'}
                        </Alert>
                    </Center>
                )}
            </TableContainer>

            {/* Pagination */}
            {filteredRewards.length > usersPerPage && (
            <Flex
                mt={6}
                justify="space-between"  // Changed from "center" to "space-between"
                align="center"
                bg={paginationBg}
                p={{ base: 3, md: 4 }}
                borderRadius="lg"
                flexDirection={{ base: "column", sm: "row" }}
                gap={{ base: 3, sm: 4 }}
                wrap="wrap"
                >
                {/* Results count - left aligned */}
                <Box 
                width={{ base: "full", sm: "auto" }}
                textAlign={{ base: "center", sm: "left" }}
                >
                <Text fontSize={{ base: "xs", sm: "sm" }}>
                    Showing {(currentPage - 1) * usersPerPage + 1} to{' '}
                    {Math.min(currentPage * usersPerPage, filteredRewards.length)} of {filteredRewards.length} transactions
                </Text>
                </Box>
            
                {/* Pagination controls - right aligned */}
                <HStack spacing={4}>
                <Button
                    size={{ base: "sm", md: "md" }}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    isDisabled={currentPage === 1}
                    colorScheme={colorScheme}
                    variant="outline"
                    leftIcon={<Icon as={FaChevronLeft} boxSize={3} />}
                >
                    <Text display={{ base: "none", sm: "block" }}>Previous</Text>
                </Button>
                
                <HStack spacing={2}>
                    <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="semibold">Page</Text>
                    <Input 
                    size={{ base: "sm", md: "md" }}
                    type="number" 
                    value={currentPage} 
                    onChange={(e) => {
                        const page = Math.min(Math.max(1, Number(e.target.value)), totalPages);
                        setCurrentPage(page);
                    }} 
                    width="60px" 
                    textAlign="center" 
                    min={1}
                    max={totalPages}
                    />
                    <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="semibold">of {totalPages}</Text>
                </HStack>
                
                <Button
                    size={{ base: "sm", md: "md" }}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    isDisabled={currentPage === totalPages}
                    colorScheme={colorScheme}
                    variant="outline"
                    rightIcon={<Icon as={FaChevronRight} boxSize={3} />}
                    >
                    <Text display={{ base: "none", sm: "block" }}>Next</Text>
                </Button>
                </HStack>
            </Flex>
            )}
            <Modal isOpen={isRedeemOpen} onClose={onRedeemClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader>
                <Stack spacing={1}>
                    <Text fontSize="lg" fontWeight="bold">
                    Redeem Details
                    </Text>
                    {/* <Text fontSize="sm" color="gray.500">
                    View redemption transaction information
                    </Text> */}
                </Stack>
                </ModalHeader>

                <ModalCloseButton />

                <ModalBody pb={6}>
                {selectedInvoice ? (
                    <Stack spacing={6}>
                    {/* Summary Section – Split Layout */}
                    <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={5}
                    >
                    <Grid templateColumns="2fr 1fr" gap={6} alignItems="center">
                        {/* Left: Redeem Info */}
                        <Stack spacing={2}>
                        <Badge
                            colorScheme="blue"
                            variant="subtle"
                            px={3}
                            py={1}
                            borderRadius="full"
                            width="fit-content"
                        >
                            <Icon as={FaHashtag} mr={1} />
                            Redeem ID
                        </Badge>

                        <Text fontSize="md" fontWeight="semibold">
                            {selectedInvoice.invoice_id}
                        </Text>

                        {/* <Text fontSize="sm" color="gray.500">
                            Redemption Transaction Summary
                        </Text> */}
                        </Stack>

                        {/* Right: Balance */}
                        <Box textAlign="center">
                        <Text fontSize="xs" color="gray.500" mb={1}>
                           Remaining Balance
                        </Text>

                        <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color={
                            selectedInvoice.type === 'redeem'
                                ? 'purple.500'
                                : 'green.500'
                            }
                        >
                            {selectedInvoice.type === 'redeem'
                            ? `${selectedInvoice.user?.current_points ?? 0}`
                            : `${selectedInvoice.user?.current_stamps ?? 0}`}
                        </Text>

                        <Badge
                            mt={1}
                            colorScheme={
                            selectedInvoice.type === 'redeem' ? 'purple' : 'green'
                            }
                            variant="subtle"
                            borderRadius="full"
                            px={3}
                        >
                            {selectedInvoice.type === 'redeem' ? 'Points' : 'Stamps'}
                        </Badge>
                        </Box>
                    </Grid>
                    </Box>

                    {/* Info Grid */}
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            <Icon as={FaUser} mr={1} />
                            Customer
                            </Text>
                            <Text fontWeight="medium">
                            {selectedInvoice.user?.name || 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>

                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            <Icon as={FaPhoneAlt} mr={1} />
                            Phone
                            </Text>
                            <Text fontWeight="medium">
                            {selectedInvoice.user?.phone || 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>

                        <GridItem colSpan={2}>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            <Icon as={FaGift} mr={1} />
                            Reward
                            </Text>
                            <Text fontWeight="medium">
                            {selectedInvoice.reward?.title || 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>
                    </Grid>

                    <Divider />

                    {/* Redeem Meta */}
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            Redeem Type
                            </Text>
                            <Badge
                            colorScheme={
                                selectedInvoice.type === 'redeem' ? 'purple' : 'green'
                            }
                            variant="subtle"
                            borderRadius="full"
                            px={3}
                            >
                            {selectedInvoice.type === 'redeem' ? 'Point' : 'Stamp'}
                            </Badge>
                        </Stack>
                        </GridItem>

                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            Points Required
                            </Text>
                            <Badge colorScheme="orange" borderRadius="full" px={3}>
                            {selectedInvoice.reward?.points_required || 0}
                            </Badge>
                        </Stack>
                        </GridItem>

                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            Amount
                            </Text>
                            <Text fontWeight="bold">
                            {selectedInvoice.amount || 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>
                    </Grid>

                    <Divider />

                    {/* Footer Info */}
                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            Processed By
                            </Text>
                            <Text fontWeight="medium">
                            {selectedInvoice.added_by_name || 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>

                        <GridItem>
                        <Stack spacing={1}>
                            <Text fontSize="sm" color="gray.500">
                            <Icon as={FaClock} mr={1} />
                            Date
                            </Text>
                            <Text fontWeight="medium">
                            {selectedInvoice.createdAt
                                ? new Date(selectedInvoice.createdAt).toLocaleString('en-GB')
                                : 'N/A'}
                            </Text>
                        </Stack>
                        </GridItem>
                    </Grid>
                    </Stack>
                ) : (
                    <Text color="gray.500">No redeem record selected</Text>
                )}
                </ModalBody>
            </ModalContent>
            </Modal>

            {/* <Modal isOpen={isRedeemOpen} onClose={onRedeemClose} size="lg" isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Redeem Details</ModalHeader>
                    <ModalCloseButton />

                    <ModalBody>
                    {selectedInvoice ? (
                        <Stack spacing={3}>
                        <Text>
                            <b>Redeem ID:</b> {selectedInvoice.invoice_id}
                        </Text>

                        <Divider />

                        <Text>
                            <b>Customer:</b> {selectedInvoice.user?.name || 'N/A'}
                        </Text>

                        <Text>
                            <b>Phone:</b> {selectedInvoice.user?.phone || 'N/A'}
                        </Text>

                        <Text>
                            <b>Reward:</b> {selectedInvoice.reward?.title || 'N/A'}
                        </Text>

                        <Text>
                            <b>Redeem Type:</b>{' '}
                            {selectedInvoice.type === 'redeem'
                            ? 'Point'
                            : selectedInvoice.type === 'redeem_stamp'
                            ? 'Stamp'
                            : 'N/A'}
                        </Text>

                        <Text>
                            <b>Points Required:</b> {selectedInvoice.reward?.points_required || 0}
                        </Text>

                        <Text>
                            <b>Amount:</b> {selectedInvoice.amount || 'N/A'}
                        </Text>

                        <Text>
                            <b>Processed By:</b> {selectedInvoice.added_by_name || 'N/A'}
                        </Text>

                        <Text>
                            <b>Date:</b>{' '}
                            {selectedInvoice.createdAt
                            ? new Date(selectedInvoice.createdAt).toLocaleString('en-GB')
                            : 'N/A'}
                        </Text>
                        </Stack>
                    ) : (
                        <Text>No invoice selected</Text>
                    )}
                    </ModalBody>
                </ModalContent>
            </Modal> */}
        </Box>
        
    );
};

export default RewardRedemption;